import PortalDocument from "./PortalDocument";
import AbstractContainer from "./AbstractContainer";
import ViewerType from "./DataType";
import heic2any from "heic2any";
import Exception from "../../shared/exception/Exception";
import Translate from "../../shared/translation/Translate";
import FunctionHelper from "../../shared/helper/FunctionHelper";

const urlCreator = FunctionHelper.applyValue(window.URL, window.webkitURL);

export interface HeicPage {
	url: string,
	width: number,
	height: number
}

export default class HeicContainer extends AbstractContainer {
	private loadingTask?: Promise<Array<HeicPage>>;

	public clean(): void {
		if (FunctionHelper.exists(this.loadingTask)) {
			this.loadingTask = undefined;
		}

		if (FunctionHelper.exists(this.data)) {
			for (let imageUrl of this.data) {
				urlCreator.revokeObjectURL(imageUrl);
			}
		}

		this.data = undefined;
		this.loaded = false;
	}

	public async loadViewerData(portalDocument: PortalDocument): Promise<Array<HeicPage>> {
		if (!FunctionHelper.exists(this.data)) {
			throw new Exception(Translate.value("errors.files.corrupt", {ns: "message"}));
		}

		if (FunctionHelper.exists(this.loadingTask)) {
			return this.loadingTask!;
		}

		if (this.loaded) {
			return this.data;
		}

		this.loadingTask = new Promise<Array<HeicPage>>(async (resolve, reject) => {
			let heicPages: Array<Blob> = await heic2any({
				blob: await new Blob([this.data]),
				multiple: true,
				toType: "image/jpeg",
				quality: 1
			}) as Array<Blob>;

			let result: Array<HeicPage> = [];
			for (let heicPage of heicPages) {
				let url: string = urlCreator.createObjectURL(heicPage);

				result.push(
					await new Promise<HeicPage>((addImage) => {
						let loadingImage: HTMLImageElement = document.createElement("img");
						loadingImage.onload = () => {
							addImage({
								url: url,
								width: loadingImage.naturalWidth,
								height: loadingImage.naturalHeight
							});
						};
						loadingImage.onerror = (event: Event | string) => {
							reject(event);
						};
						loadingImage.src = url;
					})
				);
			}

			resolve(result);
		});

		try {
			this.data = await this.loadingTask;
			this.loaded = true;
		} catch (exception: any) {
			let portalException: Exception = new Exception(
				Translate.value("errors.files.load", {ns: "message"})
			);
			portalException.originalException = exception;
			throw portalException;
		}

		return this.loadingTask;
	}

	public async getNumberOfPages(data: Array<HeicPage>) {
		return data.length;
	}

	public getType(): ViewerType {
		return ViewerType.HEIC;
	}
}